import React from 'react';
import styled from 'styled-components';
import { ButtonExportCSVAsync } from 'components/Button';
import { SectionConditional } from 'components/Section';
import { HeaderCell, HeaderRow, Row, Table, TableBody, TableHead } from 'components/Table';
import { Label, Text } from 'components/Typography';
import { SAMPLE_UPLOAD_PAYMENT_DATA } from 'config/const';
import { lineHeight, theme, toRem } from 'design';
import { IExportCSV } from 'utils/csvformatter';
import Sidebar from './components/Sidebar';
import { Content as BaseContent } from './components/styles';

interface IUploadInstructionsSidebarProps {
  dataCy?: string;
  onBack: () => void;
}

const Content = styled(BaseContent)`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  height: 100%;
  gap: ${toRem(theme.space.md)};

  .description {
    color: ${theme.color.neutral[500]};
    margin-bottom: ${toRem(theme.space.md)};

    @media (max-width: ${theme.breakpoint.large}px) {
      font-size: ${toRem(theme.font.size.xs)};
      line-height: ${lineHeight(theme.font.size.xs)};
    }
  }

  div.data-table {
    overflow-y: unset;

    table {
      border-radius: ${toRem(4)};
      overflow: hidden;
      border-collapse: separate;

      thead {
        tr {
          th:not(:first-child) {
            border-left: ${toRem(1)} solid ${theme.color.neutral[200]};
          }
        }
      }

      tbody {
        tr:not(:last-child) {
          td {
            border-bottom: ${toRem(1)} solid ${theme.color.neutral[200]};
          }
        }
      }

      th,
      td {
        padding: ${toRem(theme.space.xs)} ${toRem(theme.space.sm)};
        font-size: ${toRem(theme.font.size.xs)};
        line-height: ${lineHeight(theme.font.size.xs)};

        @media (max-width: ${theme.breakpoint.large}px) {
          font-size: ${toRem(theme.font.size.xxs)};
          line-height: ${lineHeight(theme.font.size.xxs)};
        }
      }
    }
  }
`;

const SampleDataCell = styled.td`
  &:not(:first-child) {
    border-left: 1px solid ${theme.color.neutral[200]};
  }
`;

const List = styled.ul`
  display: flex;
  flex-flow: column nowrap;
  margin-top: ${toRem(theme.space.md)};
  gap: ${toRem(theme.space.xs)};
  padding: ${toRem(theme.space.xl)} 0 0 ${toRem(theme.space.lg)};
  border-top: ${toRem(2)} solid ${theme.color.neutral[200]};
`;

const ListItem = styled.li`
  font-size: ${toRem(theme.font.size.xs)};
  color: ${theme.color.neutral[500]};
`;

const Title = styled.div`
  display: flex;
  flex-flow: column nowrap;

  button {
    padding: 0;
    color: ${theme.color.primary[500]};
    font-weight: ${theme.font.weight.regular};
  }
`;

const getExportCSV = (headers: string[]): IExportCSV => ({ headers: headers.map((header) => header.replace(' *', '')), data: [[]] });

const UploadInstructionsSideBar = ({ onBack, dataCy = 'upload-instructions-sidebar' }: IUploadInstructionsSidebarProps) => (
  <Sidebar title="Upload Instructions" leftActionButton={{ label: 'Back', appearance: 'white', onClick: onBack }} dataCy={dataCy}>
    <Content>
      <Text size="small" className="description">
        The Kickfin platform provides the ability to make payments by uploading a file in .csv format. Below is a set of instructions and downloadable templates for supported file formats.
      </Text>
      {SAMPLE_UPLOAD_PAYMENT_DATA.map((upload, index: number) => (
        <React.Fragment key={`table-${index}`}>
          <Title>
            <Label data-cy={`table-${index}-title`}>{upload.title}</Label>
            <ButtonExportCSVAsync data-cy={`table-${index}-export-csv-btn`} label="Download Template" appearance="link" exportFunc={async () => getExportCSV(upload.headers)} fileName={upload.fileName} />
          </Title>
          <Table className="data-table">
            <TableHead>
              <HeaderRow>
                {upload.headers.map((header: string, idx: number) => (
                  <HeaderCell key={`table-${index}-header-${idx}`}>{header}</HeaderCell>
                ))}
              </HeaderRow>
            </TableHead>
            <TableBody>
              {upload.data.map((row: string[], rowIndex: number) => (
                <Row key={`table-${index}-row-${rowIndex}`}>
                  {row.map((col: string, colIndex: number) => (
                    <SampleDataCell key={`table-${index}-row-${rowIndex}-col-${colIndex}`}>{col}</SampleDataCell>
                  ))}
                </Row>
              ))}
            </TableBody>
          </Table>
          {index !== SAMPLE_UPLOAD_PAYMENT_DATA.length - 1 && <SectionConditional condition="or" />}
        </React.Fragment>
      ))}
      <List data-cy={`${dataCy}-upload-instructions-list`}>
        <ListItem>The cash & notes fields are optional.</ListItem>
        <ListItem>The amount column must use dollars and cents with dollar sign optional such as $14.35 or 14.35.</ListItem>
        <ListItem>Any names with punctuation must be "enclosed in" quotation marks.</ListItem>
      </List>
    </Content>
  </Sidebar>
);

export default UploadInstructionsSideBar;
